import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useBankAccountStore } from '@/stores/bankAccountStore';
import { useBankStore } from '@/stores/bankStore';
import { apiService } from '@/services/api';
import type { BankDto } from '@/types';
import Icon from '../components/Icon';
import BankLogo from '../components/BankLogo';

// Connector for the Lusopay path — not a SIBS aspsp, so it's a synthetic entry. Its bankCode
// "LusoPay" is what the backend branches on (no SIBS consent; routes to our credentials UI).
const LUSOPAY_BANK: BankDto = {
  id: 'lusopay',
  bic: '',
  name: 'Lusopay',
  brandName: 'Lusopay',
  aspspCde: 'LusoPay',
  bankCode: 'LusoPay',
  logoLocation: undefined,
};

// Connector for Revolut — its own integration service, not a SIBS aspsp. No IBAN is asked for:
// the user picks the account at Revolut and the backend reads it back from the consent.
const REVOLUT_BANK: BankDto = {
  id: 'revolut',
  bic: 'REVOGB21',
  name: 'Revolut',
  brandName: 'Revolut',
  aspspCde: 'REVOGB21',
  bankCode: 'REVOGB21',
  logoLocation: '/bank-logos/REVOLUT.svg',
};

type ConnectorId = 'Sibs' | 'LusoPay' | 'Revolut';

// Portuguese IBAN: PT50 + 21-digit NIB; positions 5-8 (0-indexed 4-8) are the 4-digit bank code.
const cleanUpper = (s: string) => s.replace(/\s/g, '').toUpperCase();
const extractBankCode = (s: string): string | null => {
  const c = cleanUpper(s);
  if (c.startsWith('PT') && c.length >= 8) return c.substring(4, 8); // from a (partial) IBAN
  if (/^\d{4}$/.test(c)) return c; // bare NIB code
  return null;
};

const bankMatches = (bank: BankDto, q: string): boolean => {
  const lc = q.trim().toLowerCase();
  if (!lc) return true;
  const code = extractBankCode(q);
  return (
    bank.name.toLowerCase().includes(lc) ||
    bank.brandName.toLowerCase().includes(lc) ||
    bank.aspspCde.toLowerCase().includes(lc) ||
    (!!bank.bankCode &&
      (bank.bankCode.toLowerCase().includes(lc) || (!!code && bank.bankCode === code)))
  );
};

interface FormErrors {
  iban?: string;
  friendlyName?: string;
  selectedBank?: string;
  pan?: string;
}

// A PAN is 12–19 digits (spaces allowed in the input). SIBS identifies a card by its PAN.
const cleanDigits = (s: string) => s.replace(/\D/g, '');
const looksLikePan = (s: string) => {
  const d = cleanDigits(s);
  return d.length >= 12 && d.length <= 19;
};

export const AddBankAccountPage = () => {
  const navigate = useNavigate();
  const { createAccount, isLoading, error, clearError } = useBankAccountStore();
  const { banks, fetchBanks, isLoading: isBanksLoading, error: banksError } = useBankStore();

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  // Bank filter — a "Search" affordance that's collapsed by default (loupe button reveals it).
  // It ONLY finds the bank; the IBAN is entered in its own field below.
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [iban, setIban] = useState(''); // the single IBAN input (SIBS accounts)
  const [friendlyName, setFriendlyName] = useState('');
  const [selectedBank, setSelectedBank] = useState<string>('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  // Connect an IBAN bank account ('account') or a credit card ('card', PAN-based consent).
  // The card toggle UI isn't wired yet, so the setters aren't bound — 'account' is the
  // only reachable mode until the card flow ships its picker.
  const [accountType] = useState<'account' | 'card'>('account');
  const [pan] = useState('');

  // Revolut is only offered where the backend has its service configured; off if the check fails.
  const [revolutEnabled, setRevolutEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    apiService
      .get<boolean>('/revolut/status')
      .then((response) => { if (!cancelled) setRevolutEnabled(response.value === true); })
      .catch(() => { /* stays off */ });
    return () => { cancelled = true; };
  }, []);

  const connectors = useMemo(
    () => [
      { id: 'Sibs' as ConnectorId, name: 'SIBS · Open Banking', banks },
      { id: 'LusoPay' as ConnectorId, name: 'Lusopay', banks: [LUSOPAY_BANK] },
      ...(revolutEnabled ? [{ id: 'Revolut' as ConnectorId, name: 'Revolut · Open Banking', banks: [REVOLUT_BANK] }] : []),
    ],
    [banks, revolutEnabled],
  );

  // Filter country → connector → bank by the search text. A connector shows when its name matches
  // (then all its banks) or it has matching banks (then only those).
  const filteredConnectors = useMemo(() => {
    const lc = search.trim().toLowerCase();
    return connectors
      .map((c) => {
        const nameMatch = !lc || c.name.toLowerCase().includes(lc);
        const visibleBanks = nameMatch ? c.banks : c.banks.filter((b) => bankMatches(b, search));
        return { ...c, visibleBanks };
      })
      .filter((c) => c.visibleBanks.length > 0);
  }, [connectors, search]);

  const isCard = accountType === 'card';
  const isLusopay = selectedBank === 'LusoPay';
  const isRevolut = selectedBank === REVOLUT_BANK.aspspCde;
  const effectiveIban = cleanUpper(iban);

  // If the user pastes an IBAN (or NIB code) into the search box, auto-select the matching bank
  // so they don't have to hunt for it in the grid. The IBAN itself is still entered in its field.
  useEffect(() => {
    const code = extractBankCode(search);
    if (code) {
      const match = banks.find((b) => b.bankCode === code);
      if (match && match.aspspCde !== selectedBank) setSelectedBank(match.aspspCde);
    }
  }, [search, banks, selectedBank]);

  const handleSelectBank = (aspspCde: string) => {
    setSelectedBank(aspspCde);
    setTouched((p) => ({ ...p, selectedBank: true }));
    setFormErrors((p) => ({ ...p, selectedBank: undefined }));
  };

  const validate = (): boolean => {
    const errors: FormErrors = {};
    if (!selectedBank) errors.selectedBank = 'Select a connector and bank';
    if (!friendlyName.trim()) errors.friendlyName = 'Account name is required';
    else if (friendlyName.trim().length < 2) errors.friendlyName = 'At least 2 characters';
    if (isCard) {
      // A credit card is identified by its PAN (no IBAN).
      if (!looksLikePan(pan)) errors.pan = 'Enter a valid card number (12–19 digits)';
    } else if (!isLusopay && !isRevolut && (!effectiveIban || effectiveIban.length < 15)) {
      // IBAN required for SIBS accounts only.
      errors.iban = 'Enter a valid IBAN for this bank';
    }
    setFormErrors(errors);
    setTouched({ iban: true, friendlyName: true, selectedBank: true, pan: true });
    return !errors.selectedBank && !errors.friendlyName && !errors.iban && !errors.pan;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      if (isCard) {
        // Credit card: PAN-based SIBS consent (no IBAN). Bank authorization happens via SCA redirect.
        const res = await createAccount({
          bankCode: selectedBank,
          iban: '',
          friendlyName: friendlyName.trim(),
          accountType: 'card',
          pan: cleanDigits(pan),
        });
        if (res.scaRedirectUrl) {
          window.location.href = res.scaRedirectUrl;
        } else {
          navigate('/');
        }
        return;
      }

      if (isLusopay) {
        // No SIBS consent / IBAN — create the account, then go to the credentials interface.
        const res = await createAccount({ bankCode: 'LusoPay', iban: '', friendlyName: friendlyName.trim() });
        navigate(`/accounts/${res.accountId}?setup=lusopay`);
        return;
      }

      const res = await createAccount({ bankCode: selectedBank, iban: effectiveIban, friendlyName: friendlyName.trim() });
      if (res.scaRedirectUrl) {
        window.location.href = res.scaRedirectUrl; // SIBS bank authorization
      } else if (res.consentStatus?.toLowerCase() === 'valid') {
        // Covered by the consent another account at this bank holds: no SCA, just finalize and sync.
        navigate(`/consent/callback?accountId=${res.accountId}`);
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Failed to create account', err);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 flex flex-1 justify-center py-5 md:py-10">
      <div className="flex flex-col w-full max-w-4xl flex-1">
        {/* Breadcrumb */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <Link to="/" className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary text-sm font-medium transition-colors">
            Accounts
          </Link>
          <Icon name="chevron_right" size={16} className="text-slate-400 dark:text-slate-500" />
          <span className="text-slate-900 dark:text-slate-100 text-sm font-medium">Add New Account</span>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-3">Connect a New Account</h1>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Choose your bank below (or use the search to filter), then enter the account IBAN. Or choose
            Lusopay to connect with merchant credentials.
          </p>
        </div>

        {/* API Error */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="error" className="text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
            </div>
            <button onClick={clearError} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200">
              <Icon name="close" />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-xl border border-slate-200 dark:border-slate-800">
            <div className="mx-auto max-w-lg space-y-6">
              {/* Bank picker. The search is collapsed by default and revealed by the loupe. */}
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm font-medium text-slate-900 dark:text-white">
                    Select a connector and bank <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setSearchOpen((o) => !o)}
                    aria-label="Search"
                    aria-expanded={searchOpen}
                    title="Search"
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border transition ${
                      searchOpen
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-primary hover:text-primary'
                    }`}
                  >
                    <Icon name="search" size={18} />
                  </button>
                </div>

                {searchOpen && (
                  <div>
                    <label className="sr-only" htmlFor="bank-search">Search</label>
                    <div className="relative">
                      <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                      <input
                        id="bank-search"
                        autoFocus
                        className="w-full rounded-lg border border-slate-300 dark:border-slate-700 focus:border-primary focus:ring-primary/50 focus:ring-2 bg-background-light dark:bg-background-dark p-3 pl-10 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
                        placeholder="Search by name, code, or IBAN — e.g. Millennium, 0033, PT50 0033…"
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {isBanksLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Icon name="progress_activity" size={30} className="animate-spin text-primary" />
                    <span className="ml-2 text-slate-600 dark:text-slate-400">Loading banks…</span>
                  </div>
                ) : banksError ? (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                    <Icon name="error" className="text-red-600 dark:text-red-400" />
                    <span className="text-red-800 dark:text-red-200">{banksError}</span>
                  </div>
                ) : filteredConnectors.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">No matches for “{search}”.</p>
                ) : (
                  <div className="space-y-4">
                    {/* Only PT is supported today; shown as the country group. */}
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      <span className="font-mono">PT</span> Portugal
                    </div>

                    {filteredConnectors.map((connector) => (
                      <div key={connector.id} className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                          <Icon name="account_tree" size={16} className="text-slate-400" />
                          {connector.name}
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {connector.visibleBanks.map((bank) => (
                            <button
                              key={bank.aspspCde}
                              type="button"
                              onClick={() => handleSelectBank(bank.aspspCde)}
                              className={`flex flex-col items-center justify-center gap-1.5 rounded-lg p-3 transition ${
                                selectedBank === bank.aspspCde
                                  ? 'border-2 border-primary bg-primary/10'
                                  : 'border border-slate-300 dark:border-slate-700 bg-background-light dark:bg-background-dark hover:border-primary hover:bg-primary/10'
                              }`}
                            >
                              <BankLogo code={bank.aspspCde} src={bank.logoLocation} name={bank.brandName || bank.name} className="h-14 w-14 rounded-md" />
                              <span className="text-xs font-medium text-slate-900 dark:text-white text-center">{bank.brandName || bank.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {touched.selectedBank && formErrors.selectedBank && (
                  <p className="text-sm text-red-500 flex items-center gap-1"><Icon name="error" size={16} />{formErrors.selectedBank}</p>
                )}
              </div>

              {/* IBAN — SIBS accounts only. The single place to enter the account IBAN. */}
              {selectedBank && !isLusopay && !isRevolut && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-900 dark:text-white" htmlFor="iban">
                    IBAN <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="iban"
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-700 focus:border-primary focus:ring-primary/50 focus:ring-2 bg-background-light dark:bg-background-dark p-3 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 font-mono"
                    placeholder="PT50 0002 0123 1234 5678 9015 4"
                    type="text"
                    value={iban}
                    onChange={(e) => setIban(e.target.value.toUpperCase())}
                  />
                  {touched.iban && formErrors.iban ? (
                    <p className="text-sm text-red-500 flex items-center gap-1"><Icon name="error" size={16} />{formErrors.iban}</p>
                  ) : (
                    <p className="text-xs text-slate-500 dark:text-slate-400">Open Banking needs the account IBAN to request consent.</p>
                  )}
                </div>
              )}

              {/* Account name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-900 dark:text-white" htmlFor="account-name">
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="account-name"
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 focus:border-primary focus:ring-primary/50 focus:ring-2 bg-background-light dark:bg-background-dark p-3 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
                  placeholder="e.g., My Savings Account"
                  type="text"
                  value={friendlyName}
                  onChange={(e) => setFriendlyName(e.target.value)}
                />
                {touched.friendlyName && formErrors.friendlyName && (
                  <p className="text-sm text-red-500 flex items-center gap-1"><Icon name="error" size={16} />{formErrors.friendlyName}</p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-lg bg-primary px-6 py-3 text-base font-bold text-white transition hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
              >
                {isLoading ? (
                  <><Icon name="progress_activity" className="animate-spin" />Connecting…</>
                ) : isLusopay ? (
                  <><Icon name="key" />Continue to credentials</>
                ) : (
                  <><Icon name="link" />Connect Account</>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Security Notice */}
        <div className="mt-8 p-6 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 flex items-start gap-4">
          <div className="flex-shrink-0 text-green-600 dark:text-green-400"><Icon name="verified_user" size={30} /></div>
          <div className="flex flex-col gap-2">
            <h3 className="text-slate-900 dark:text-white font-bold text-lg">Your Data is Secure</h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              Open Banking connections are read-only — we can never move money on your behalf. Lusopay credentials are
              stored encrypted and never shown again.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
